import { useTypedSelector } from 'src/State/RootReducer'

type Emissions = Record<number, number | undefined>

/**
 * Returns the amount of saved CO2 in gramm.
 * @param year year of the CO2 savings
 * @param selfProduction produced energy in watt in the given year
 * @returns g of saved CO2
 */
export const useCo2Savings = (year: number, selfProduction: number | undefined | null): number => {
  const emissionFactor = useEmissionFactor(year) ?? 0
  /* Selfproduction in W / 1000 um kw zu bekommen => Faktor ist gramm je kW */
  return selfProduction ? (selfProduction / 1000) * emissionFactor : 0
}

/**
 * Returns the CO2 emission factor from https://www.eea.europa.eu/ims/greenhouse-gas-emission-intensity-of-1.
 * The factor shows how many g of CO2 was on average saved in a specific country on a specific year.
 * "DEU" is used as default country and the last year provided by the site are used if no data is available.
 * @param year year of the co2 savings
 * @returns emission factor in g per kWh
 */
export const useEmissionFactor = (year: number): number | undefined => {
  const installationCountryCode = useTypedSelector(state => state.storage.installation?.countryCode) ?? 'DEU'

  if (installationCountryCode) {
    const countryInCaps = installationCountryCode.toUpperCase()
    const countryValues = emissionsPerYearCountry[countryInCaps]
    const countryValuesFallback = emissionsPerYearCountry['DEU']

    return getLastAvailableEmissionFactor(year, countryValues ?? countryValuesFallback)
  }
}

/**
 * Returns the last available emission factor from the emissionsPerYearCountry object
 * @param year year of the emission factor
 * @param object emission factors by year
 * @returns emission factor in g per kWh
 */
const getLastAvailableEmissionFactor = (year: number, object: Emissions): number | undefined => {
  const value = object[year]
  if (!value) {
    const newYear = year - 1
    if (newYear >= 2010) {
      return getLastAvailableEmissionFactor(newYear, object)
    }
  }
  return value
}

/**
 * values from https://www.eea.europa.eu/ims/greenhouse-gas-emission-intensity-of-1 in g per kwh for multiple european countries
 */
const emissionsPerYearCountry: Record<string, Emissions> = {
  AUT: {
    //Österreich
    2010: 153,
    2011: 160,
    2012: 116,
    2013: 106,
    2014: 92,
    2015: 107,
    2016: 94,
    2017: 103,
    2018: 100,
    2019: 92,
    2020: 82.4,
    2021: undefined,
  },
  BEL: {
    //Belgien
    2010: 161,
    2011: 190,
    2012: 149,
    2013: 75,
    2014: 169,
    2015: 136,
    2016: 170,
    2017: 179,
    2018: 207,
    2019: 174,
    2020: 161,
    2021: undefined,
  },
  BGR: {
    //Bulgarien
    2010: 534,
    2011: 587,
    2012: 528,
    2013: 485,
    2014: 479,
    2015: 487,
    2016: 467,
    2017: 499,
    2018: 427,
    2019: 427,
    2020: 410.4,
    2021: undefined,
  },
  HRV: {
    //Kroatien
    2010: 204,
    2011: 295,
    2012: 280,
    2013: 198,
    2014: 168,
    2015: 203,
    2016: 204,
    2017: 188,
    2018: 130,
    2019: 163,
    2020: 133.8,
    2021: undefined,
  },
  CYP: {
    //Zypern
    2010: 735,
    2011: 759,
    2012: 757,
    2013: 666,
    2014: 682,
    2015: 670,
    2016: 679,
    2017: 661,
    2018: 664,
    2019: 642,
    2020: 620.9,
    2021: undefined,
  },
  CZE: {
    //Tschechien
    2010: 557,
    2011: 547,
    2012: 510,
    2013: 478,
    2014: 481,
    2015: 493,
    2016: 503,
    2017: 472,
    2018: 465,
    2019: 433,
    2020: 436.6,
    2021: undefined,
  },
  DNK: {
    //Dänemark
    2010: 373,
    2011: 336,
    2012: 282,
    2013: 317,
    2014: 277,
    2015: 204,
    2016: 236,
    2017: 179,
    2018: 193,
    2019: 123,
    2020: 109,
    2021: undefined,
  },
  EST: {
    //Estland
    2010: 948,
    2011: 963,
    2012: 905,
    2013: 963,
    2014: 967,
    2015: 949,
    2016: 912,
    2017: 902,
    2018: 874,
    2019: 733,
    2020: 774.9,
    2021: undefined,
  },
  FIN: {
    //Finnland
    2010: 215,
    2011: 173,
    2012: 124,
    2013: 158,
    2014: 138,
    2015: 99,
    2016: 103,
    2017: 93,
    2018: 107,
    2019: 84,
    2020: 68.6,
    2021: undefined,
  },
  FRA: {
    //Frankreich
    2010: 77,
    2011: 66,
    2012: 71,
    2013: 71,
    2014: 53,
    2015: 54,
    2016: 60,
    2017: 69,
    2018: 58,
    2019: 56,
    2020: 51.1,
    2021: undefined,
  },
  DEU: {
    //Deutschland
    2010: 471,
    2011: 478,
    2012: 483,
    2013: 489,
    2014: 479,
    2015: 448,
    2016: 445,
    2017: 413,
    2018: 404,
    2019: 344,
    2020: 311,
    2021: 402,
  },
  GRC: {
    //Griechenland
    2010: 875,
    2011: 883,
    2012: 869,
    2013: 808,
    2014: 846,
    2015: 739,
    2016: 662,
    2017: 657,
    2018: 654,
    2019: 606,
    2020: 479.2,
    2021: undefined,
  },
  HUN: {
    //Ungarn
    2010: 306,
    2011: 315,
    2012: 320,
    2013: 288,
    2014: 273,
    2015: 274,
    2016: 263,
    2017: 266,
    2018: 251,
    2019: 227,
    2020: 216.4,
    2021: undefined,
  },
  ISL: {
    //Island
    2010: 1,
    2011: undefined,
    2012: undefined,
    2013: undefined,
    2014: undefined,
    2015: undefined,
    2016: undefined,
    2017: undefined,
    2018: undefined,
    2019: undefined,
    2020: undefined,
    2021: undefined,
  },
  IRL: {
    //Irland
    2010: 485,
    2011: 456,
    2012: 487,
    2013: 460,
    2014: 450,
    2015: 437,
    2016: 429,
    2017: 399,
    2018: 353,
    2019: 316,
    2020: 278.6,
    2021: undefined,
  },
  ITA: {
    //Italien
    2010: 339,
    2011: 328,
    2012: 312,
    2013: 273,
    2014: 258,
    2015: 284,
    2016: 263,
    2017: 265,
    2018: 249,
    2019: 234,
    2020: 213.4,
    2021: undefined,
  },
  LVA: {
    //Lettland
    2010: 121,
    2011: 131,
    2012: 90,
    2013: 128,
    2014: 127,
    2015: 146,
    2016: 129,
    2017: 75,
    2018: 146,
    2019: 160,
    2020: 106.5,
    2021: undefined,
  },
  LTU: {
    //Litauen
    2010: 308,
    2011: 261,
    2012: 269,
    2013: 199,
    2014: 179,
    2015: 186,
    2016: 143,
    2017: 84,
    2018: 54,
    2019: 72,
    2020: 45.4,
    2021: undefined,
  },
  LUX: {
    //Luxemburg
    2010: 228,
    2011: 230,
    2012: 238,
    2013: 189,
    2014: 198,
    2015: 137,
    2016: 71,
    2017: 64,
    2018: 65,
    2019: 73,
    2020: 58.5,
    2021: undefined,
  },
  MLT: {
    //Malta
    2010: 884,
    2011: 879,
    2012: 883,
    2013: 731,
    2014: 715,
    2015: 654,
    2016: 653,
    2017: 435,
    2018: 356,
    2019: 357,
    2020: 379,
    2021: undefined,
  },
  NLD: {
    //Niederlande
    2010: 442,
    2011: 430,
    2012: 456,
    2013: 471,
    2014: 449,
    2015: 506,
    2016: 487,
    2017: 460,
    2018: 440,
    2019: 392,
    2020: 328.4,
    2021: undefined,
  },
  POL: {
    //Polen
    2010: 850,
    2011: 852,
    2012: 828,
    2013: 828,
    2014: 816,
    2015: 792,
    2016: 786,
    2017: 778,
    2018: 784,
    2019: 744,
    2020: 709.8,
    2021: undefined,
  },
  PRT: {
    //Portugal
    2010: 280,
    2011: 333,
    2012: 397,
    2013: 318,
    2014: 299,
    2015: 364,
    2016: 296,
    2017: 363,
    2018: 310,
    2019: 255,
    2020: 198.4,
    2021: undefined,
  },
  ROU: {
    //Rumänien
    2010: 301,
    2011: 390,
    2012: 449,
    2013: 291,
    2014: 268,
    2015: 294,
    2016: 308,
    2017: 326,
    2018: 313,
    2019: 289,
    2020: 299.5,
    2021: undefined,
  },
  SVK: {
    //Slowakei
    2010: 142,
    2011: 152,
    2012: 150,
    2013: 131,
    2014: 118,
    2015: 126,
    2016: 118,
    2017: 113,
    2018: 136,
    2019: 122,
    2020: 101.7,
    2021: undefined,
  },
  SVN: {
    //Slowenien
    2010: 334,
    2011: 343,
    2012: 335,
    2013: 311,
    2014: 218,
    2015: 256,
    2016: 256,
    2017: 254,
    2018: 241,
    2019: 238,
    2020: 217.8,
    2021: undefined,
  },
  ESP: {
    //Spain
    2010: 257,
    2011: 320,
    2012: 333,
    2013: 270,
    2014: 280,
    2015: 319,
    2016: 267,
    2017: 306,
    2018: 276,
    2019: 210,
    2020: 156.4,
    2021: undefined,
  },
  SWE: {
    //Schweden
    2010: 23,
    2011: 16,
    2012: 11,
    2013: 11,
    2014: 9,
    2015: 10,
    2016: 10,
    2017: 10,
    2018: 11,
    2019: 10,
    2020: 8.8,
    2021: undefined,
  },
  GBR: {
    //England
    2010: 463,
    2011: 446,
    2012: 494,
    2013: 459,
    2014: 416,
    2015: 366,
    2016: 288,
    2017: 255,
    2018: 242,
    2019: 225,
    2020: undefined,
    2021: undefined,
  },
}
